/*
 * A conversa aberta (ChannelView do ramon/chat) com o que o chat do tema tem
 * a mais:
 *
 * - (11) a estrela do canal favorito no cabeçalho;
 * - (18) mensagens excluídas seguidas viram uma linha só, "N mensagens foram
 *   excluídas. [ver todas]";
 * - (19) o ponto na faixa da fixada quando há fixada que o leitor ainda não
 *   viu (a última vista fica no navegador, por canal) — com a faixa nova
 *   (`pin_bar`, pins.tsx), o ponto mora no "ver todas" dela;
 * - (22) "está digitando" sem websocket: sem o flarum/realtime, quem tem a
 *   conversa aberta pergunta ao servidor a cada poucos segundos
 *   (extend/ramonchat.php guarda o sinal no cache).
 *
 * O ChannelView mora no chunk `chatUi`: os extends usam o nome do módulo.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import classList from 'flarum/common/utils/classList';
import { apiUrl, chatState, childList, component, ct, feature, findByClass, pushChild, readLocal, rt, writeLocal } from './flags';
import { isStarred, toggleStar } from './starred';
import { pinBarOn } from './pins';

// ─── (19) Fixadas não vistas ────────────────────────────────────────────────

function seenPins() {
  return readLocal('pinsSeen', {});
}
function markPinSeen(channelId, messageId) {
  const seen = seenPins();
  if ((seen[channelId] || 0) >= messageId) return;
  seen[channelId] = messageId;
  writeLocal('pinsSeen', seen);
}

/** Há fixada mais nova que a última que o leitor abriu neste canal. */
export function hasUnseenPin(channelId) {
  const pinned = chatState()?.latestPinned?.(channelId);
  if (!pinned) return false;
  return Number(pinned.id()) > (seenPins()[channelId] || 0);
}

// ─── (18) Excluídas em sequência ────────────────────────────────────────────

/** As sequências abertas pelo leitor ("ver todas"), pela primeira mensagem. */
const expandedRuns = new Set();
function isDeletedRow(vnode) {
  const message = vnode?.attrs?.message;
  return !!message && typeof message.isDeleted === 'function' && !!message.isDeleted() && !message.isSystem?.();
}
function collapseDeleted(rows) {
  const out = [];
  for (let i = 0; i < rows.length;) {
    if (!isDeletedRow(rows[i])) {
      out.push(rows[i]);
      i++;
      continue;
    }
    let end = i;
    while (end < rows.length && isDeletedRow(rows[end])) end++;
    const run = rows.slice(i, end);
    const key = `deleted-${run[0].attrs.message.id()}`;
    if (run.length < 2 || expandedRuns.has(key)) {
      out.push(...run);
    } else {
      out.push(m("div", {
        key: key,
        className: "ChatMessage ChatMessage--system CanelaRamonChat-deletedRun"
      }, m("div", {
        className: "ChatMessage-gutter"
      }), m("div", {
        className: "ChatMessage-body"
      }, m("button", {
        type: "button",
        className: "ChatMessage-tombstone CanelaRamonChat-deletedRun-button",
        onclick: () => {
          expandedRuns.add(key);
        }
      }, ct('deleted', {
        count: run.length
      })))));
    }
    i = end;
  }
  return out;
}

// ─── (22) Digitando, por polling ────────────────────────────────────────────

const POLL_MS = 3000;
let pollTimer = null;
let polledChannel = null;
function realtimeOn() {
  return 'flarum-realtime' in flarum.extensions;
}
function pollTyping() {
  pollTimer = null;
  const state = chatState();
  const channelId = polledChannel;
  if (!state || channelId === null || !feature('typing_poll') || realtimeOn()) return;

  // Só com a conversa à vista; a aba escondida espera.
  if (!document.hidden && document.querySelector('.ChatChannel')) {
    app.request({
      method: 'GET',
      url: apiUrl(`/canela-ramon-chat/channels/${channelId}/typing`),
      errorHandler: () => {}
    }).then(result => {
      (result?.data || []).forEach(entry => state.noteTyping(channelId, entry.userId, entry.username, true, 5));
      m.redraw();
    }).catch(() => {});
  }
  pollTimer = window.setTimeout(pollTyping, POLL_MS);
}
function startTypingPoll(channelId) {
  if (realtimeOn() || !feature('typing_poll')) return;
  polledChannel = channelId;
  if (pollTimer === null) pollTimer = window.setTimeout(pollTyping, POLL_MS);
}
function stopTypingPoll(channelId) {
  if (polledChannel !== channelId) return;
  polledChannel = null;
  if (pollTimer !== null) window.clearTimeout(pollTimer);
  pollTimer = null;
}
export default function installChannelView() {
  extend(component('ChannelView'), 'header', function (vnode) {
    if (!vnode || !feature('starred')) return;
    const channel = this.attrs.channel;
    const actions = findByClass(vnode, 'ChatChannel-headerActions');
    if (!actions || !channel) return;
    const starred = isStarred(channel.id());
    const label = starred ? rt('star.remove') : ct('channel_settings.star_channel');
    pushChild(childList(actions), m(Button, {
      className: classList('Button Button--icon Button--flat CanelaRamonChat-star', {
        'ChatChannel-headerAction--active': starred
      }),
      icon: starred ? 'fas fa-star' : 'far fa-star',
      title: label,
      "aria-label": label,
      "aria-pressed": starred ? 'true' : 'false',
      onclick: () => toggleStar(channel.id())
    }), 'canela-star', 0);
  });
  extend(component('ChannelView'), 'pinnedBar', function (vnode) {
    if (!vnode || typeof vnode !== 'object' || !feature('pin_indicator') || pinBarOn()) return;
    const channelId = Number(this.attrs.channel.id());
    const pinned = chatState()?.latestPinned?.(channelId);
    if (!pinned) return;
    const markSeen = () => markPinSeen(channelId, Number(pinned.id()));
    vnode.attrs = vnode.attrs || {};

    // Abrir a fixada (pular até ela ou ver todas) conta como ver.
    const previous = vnode.attrs.onclick;
    vnode.attrs.onclick = e => {
      markSeen();
      previous?.(e);
    };
    if (!hasUnseenPin(channelId)) return;
    const key = 'className' in vnode.attrs || !('class' in vnode.attrs) ? 'className' : 'class';
    vnode.attrs[key] = `${vnode.attrs[key] || ''} CanelaRamonChat-pinnedBar--unseen`.trim();
    const jump = findByClass(vnode, 'ChatChannel-pinnedBar-jump');
    if (jump) childList(jump).push(m("span", {
      className: "CanelaRamonChat-unseenDot",
      title: rt('pins.unseen'),
      "aria-label": rt('pins.unseen')
    }));
  });

  // O painel das fixadas aberto também conta como ver todas.
  extend(component('PinnedPanel'), 'oncreate', function () {
    if (pinBarOn()) return;
    const channelId = Number(this.attrs?.channel?.id?.());
    const pinned = channelId ? chatState()?.latestPinned?.(channelId) : null;
    if (pinned) markPinSeen(channelId, Number(pinned.id()));
  });
  override(component('ChannelView'), 'rows', function (original, ...args) {
    const rows = original(...args);
    return feature('collapse_deleted') && Array.isArray(rows) ? collapseDeleted(rows) : rows;
  });
  extend(component('ChannelView'), 'oncreate', function () {
    startTypingPoll(Number(this.attrs.channel.id()));
  });
  extend(component('ChannelView'), 'onupdate', function () {
    const id = Number(this.attrs.channel.id());
    if (polledChannel !== id) startTypingPoll(id);
  });
  extend(component('ChannelView'), 'onremove', function () {
    stopTypingPoll(Number(this.attrs.channel.id()));
  });
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/ramonChat/features/channelView', installChannelView);